import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, Box, Button, TextField, Paper, IconButton, InputAdornment, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import useUserProfile from '../hooks/useUserProfile';
import SupplementsTable from './SupplementsTable';
import { color, r } from '../theme/tokens';
import EditIcon from '@mui/icons-material/Edit';
import { buttonStyles } from './common/ButtonStyles';
import config from '../config';
import { saveOrUpdateStack } from '../firebase/stackActions';
import { logAnalyticsEvent } from '../utils/analytics';
import StackComposer from './common/StackComposer';
import AgentActivityPanel from './common/AgentActivityPanel';
import { useStackGeneration } from '../contexts/StackGenerationContext';
import useStackAnalysis, { ANALYTICS_SURFACE, JOB_KIND } from './analyze/useStackAnalysis';
import {
  analysisSignature,
  forgetPendingRows,
  mergeAnalysis,
  recallPendingRows,
  rememberPendingRows,
  unanalyzedIds,
} from './analyze/mergeAnalysis';
import {
  ANALYSIS_BUSY_ANALYZING,
  ANALYSIS_BUSY_GENERATING,
  ANALYSIS_FAILED,
  ANALYSIS_LIST_CHANGED,
  ANALYZING,
  COMPOSER_SUBMIT_LABEL,
  GOALS_LABEL,
  GOALS_REQUIRED,
  PAGE_TITLE,
  SUBMIT_CTA,
  SAVE_HINT_NEEDS_NAME,
  SUPPLEMENTS_HEADING,
  SUPPLEMENTS_REQUIRED,
} from './analyze/copy';
import {
  AnalyzeHint,
  AnalyzeTitle,
  SECTION_HEADING_SX,
  StackNameField,
  RequiredMark,
  nameFieldErrorProps,
  analyzeHintText,
} from './analyze/AnalyzeFormParts';

/**
 * `origin` recorded on a stack this flow creates. Edit mode reads it to decide
 * whether saving re-runs the analysis (ruled): only stacks built here re-run;
 * every other stack saves exactly as it did before.
 */
export const ANALYZE_ORIGIN = 'analyze';

const auth = getAuth();

function StackForm({ isEditMode, stackId }) {
  const [stackName, setStackName] = useState('');
  // Save was disabled until the stack had a name, with nothing at the field
  // saying so. It now stays pressable: pressed without a name, it marks the
  // field, says why under it and puts the cursor there (2026-10-04).
  const [nameMissing, setNameMissing] = useState(false);
  const nameInputRef = useRef(null);
  const handleNameChange = (event) => {
    setStackName(event.target.value);
    if (event.target.value.trim()) setNameMissing(false);
  };
  /** True when the stack has a name; otherwise flags the field and focuses it. */
  const requireName = () => {
    if (stackName && stackName.trim()) return true;
    setNameMissing(true);
    nameInputRef.current?.focus();
    nameInputRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    return false;
  };
  const [healthGoals, setHealthGoals] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [editModeUI, setEditModeUI] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  // An analysis note that is not an error — today only "some supplements
  // changed while this ran". It was drawn in the red error style, which read
  // as a failure on a run that had worked (Tanu's staging test, case C1).
  const [analysisNotice, setAnalysisNotice] = useState('');
  const [healthGoalsId, setHealthGoalsId] = useState('');
  const [notesId, setNotesId] = useState('');
  const { userProfile, loading } = useUserProfile();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [showStackDetails, setShowStackDetails] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [citationsData, setCitationsData] = useState(null);
  const [supplementOptions, setSupplementOptions] = useState([]);
  const [stackVersionState, setStackVersionState] = useState(config.STACK_VERSION);
  // `origin` of the stack being edited. Only a stack this flow created re-runs
  // its analysis on save; everything else saves exactly as it did before.
  const [stackOrigin, setStackOrigin] = useState(isEditMode ? null : ANALYZE_ORIGIN);
  // `analysisSignature` of the goal and rows the results on screen were
  // computed from; null until an analysis has landed. In create mode it is
  // what turns the CTA from Analyze into Save — and back again (below).
  const [analyzedSignature, setAnalyzedSignature] = useState(null);

  const { analyze } = useStackAnalysis();
  const {
    status: jobStatus,
    result: jobResult,
    jobId,
    kind: jobKind,
    startedBy: jobStartedBy,
    progress: liveProgress,
    error: jobError,
    clearJob,
  } = useStackGeneration();

  /**
   * Which form this is, as the provider's `startedBy` records it: the analyze
   * page, or one particular stack's edit page. Both are this component, so
   * `kind` cannot tell them apart and this key has to.
   */
  const formKey = isEditMode ? `stack:${stackId}` : 'new';

  const consumedJobRef = useRef(null);
  // The signature of the request in flight, for the rare consume that finds no
  // pending-rows record to read it from (sessionStorage unavailable).
  const requestedSignatureRef = useRef(null);
  // The job id this mount started. It tells the consume effect whether an
  // empty list means "remounted, restore the rows" or "the member removed
  // them" — see the consume effect.
  const startedJobIdRef = useRef(null);
  // Set when the analysis is being re-run as part of an edit-mode save, so the
  // completion handler saves rather than just rendering. Deliberately NOT
  // persisted across a remount: saving a member's stack because of a button
  // they pressed before navigating away would be a surprise. After a remount
  // the results render and the member presses Update themselves.
  const saveAfterAnalysisRef = useRef(false);

  /**
   * **Ownership is read from the provider, never from a ref.**
   *
   * This started as `ownJobRef.current`, set when this form kicked the job
   * off — which is wrong the moment the member navigates away and comes back,
   * because a ref resets on unmount. The remounted form then disowned its own
   * in-flight analysis: no progress panel, the result never consumed, and
   * pressing Analyze did nothing at all, because `useStackAnalysis` refuses to
   * start a second job while one is in flight and this form deliberately shows
   * no error for that case. Silently inert until the job expired. Found by
   * PR-Agent, not by any test here, so there is now one for it.
   *
   * **`kind` is necessary and not sufficient.** It survives a remount, but
   * this component is also every stack's edit page, so "an analyze job exists"
   * does not mean "this form started it". Reading ownership from `kind` alone
   * let an analysis started on the analyze page be consumed by whichever edit
   * page mounted next: a generated stack showed another list's progress,
   * locked its Update button, then lost its own citations to the other list's
   * — one press of Update from Firestore (STACK-544, found reproducing it
   * locally). So the provider also records which form started the job, and
   * both have to match.
   */
  const ownsJob = jobKind === JOB_KIND && jobStartedBy === formKey;
  const isAnalyzing = ownsJob && (jobStatus === 'starting' || jobStatus === 'pending');
  /**
   * Whether the results on screen still describe what is on screen.
   *
   * A one-way "has results" flag was not enough: once an analysis landed the
   * CTA said Save for good, so a member who used Add amount, changed the goal
   * or added a supplement saved results about a list they no longer had — and
   * the Add amount row kept saying Add amount beside the amount they had just
   * added, with no way to get it analysed.
   */
  const resultsCurrent =
    analyzedSignature !== null
    && analyzedSignature === analysisSignature(healthGoals, selectedSupplements);
  /**
   * Create mode IS the Analyze Current Stack flow (STACK-544). In edit mode
   * only a stack this flow created re-runs its analysis on save.
   */
  const reanalyzesOnSave = !isEditMode || stackOrigin === ANALYZE_ORIGIN;

  useEffect(() => {
    if (isEditMode && stackId) {
      const fetchStack = async () => {
        try {
          const docRef = doc(db, 'stacks', stackId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const stackData = docSnap.data();
            setStackName(stackData.name);
            setSelectedSupplements(stackData.supplements);
            setCitationsData(stackData.citations || null);
            setStackVersionState(stackData.version || config.STACK_VERSION);
            setStackOrigin(stackData.origin || null);

            // Fetch health goals (check ref validity)
            if (stackData.healthGoalsRef) {
              const healthGoalsRef = doc(db, 'healthGoals', stackData.healthGoalsRef);
              const healthGoalsSnap = await getDoc(healthGoalsRef);
              if (healthGoalsSnap.exists()) {
                setHealthGoals(healthGoalsSnap.data().goals);
                setHealthGoalsId(healthGoalsRef.id);
              } else {
                console.warn(`[Edit Mode Load] Health goals document not found for ref: ${stackData.healthGoalsRef}`);
              }
            } else {
               console.warn("[Edit Mode Load] healthGoalsRef missing in stack data.");
            }

            // Fetch notes (check ref validity)
            if (stackData.notesRef) {
              if (typeof stackData.notesRef === 'string' && stackData.notesRef.trim() !== '') {
                const notesDocReference = doc(db, 'notes', stackData.notesRef);
                const notesSnap = await getDoc(notesDocReference);
                if (notesSnap.exists()) {
                  setNotes(notesSnap.data().content);
                  setNotesId(notesDocReference.id);
                } else {
                  console.warn(`[Edit Mode Load] Notes document not found for ref: ${stackData.notesRef}`);
                  setNotesId('');
                }
              } else {
                 console.error(`[Edit Mode Load] Invalid notesRef found in stack data:`, stackData.notesRef);
                 setNotesId('');
              }
            } else {
               console.warn("[Edit Mode Load] notesRef missing in stack data.");
               setNotesId('');
            }

          } else {
             console.error(`[Edit Mode Load] Stack document not found for ID: ${stackId}`);
          }
        } catch (error) {
          console.error('[Edit Mode Load] Error fetching stack:', error);
        } finally {
          setIsInitialLoadComplete(true);
        }
      };

      fetchStack();
    } else {
      setCitationsData(null);
      setIsInitialLoadComplete(true);
    }
  }, [isEditMode, stackId]);

  useEffect(() => {
    if (!isInitialLoadComplete) return;

    const params = new URLSearchParams(window.location.search);
    const tempId = params.get('tempId');
    
    if (tempId) {
      const savedStack = localStorage.getItem(`unsavedStack_${tempId}`);
      
      if (savedStack) {
        try {
          const parsedStack = JSON.parse(savedStack);
          const { supplements, stackName: savedName, healthGoals: savedGoals, notes: savedNotes, timestamp } = parsedStack;
          
          const oneHour = 60 * 60 * 1000;
          if (new Date().getTime() - timestamp < oneHour) {
            setSelectedSupplements(prev => supplements || prev);
            setStackName(prev => savedName || prev);
            setHealthGoals(prev => savedGoals || prev);
            setNotes(prev => savedNotes || prev);
            setShowStackDetails(true);
            
            // Clear tempId from URL without page reload
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('tempId');
            window.history.replaceState({}, '', newUrl);
          }
        } catch (error) {
          console.error('Error restoring stack state:', error);
        }
      }
    }
  }, [isInitialLoadComplete]);

  /**
   * Persist the stack. Split out of `handleSubmit` because an analysed stack
   * reaches it from two places — the member pressing Save, and the completion
   * of a re-run analysis during an edit-mode save — and one save path is the
   * only way those two cannot drift about what gets written.
   *
   * `rows` defaults to current state; the analysis completion handler passes
   * the freshly merged rows explicitly, because a `setState` in the same tick
   * has not landed yet.
   */
  const persistStack = async (rows = selectedSupplements, citations = citationsData) => {
    const user = auth.currentUser;

    if (!requireName()) return false;

    if (user) {
      try {
        // Prepare the CORE stack data object
        // IMPORTANT: Strip out interaction data - it should never be saved to Firestore
        // Also filter only selected supplements
        const supplementsWithoutInteractions = rows
          .filter(s => s.selected !== false)
          .map(supplement => {
            const { interaction, selected, ...supplementWithoutInteraction } = supplement;
            return supplementWithoutInteraction;
          });

        // Validate that at least one supplement is included
        if (supplementsWithoutInteractions.length === 0) {
          setErrorMessage(SUPPLEMENTS_REQUIRED);
          return false;
        }

        const stackCoreData = {
          name: stackName,
          supplements: supplementsWithoutInteractions,
          // Results travel with the stack, exactly as a generated stack stores
          // them: stack-level `citations` here, and the per-supplement
          // `evidenceScore` / `study_count` / `studyDose` / `entryState`
          // already on each row above. This is what makes reopening the stack
          // show the analysis instead of a bare list.
          ...(citations ? { citations } : {}),
          ...(!isEditMode && {
            origin: ANALYZE_ORIGIN,
            version: config.STACK_VERSION,
          }),
        };

        // Call the centralized function to save or update the stack
        await saveOrUpdateStack(
          stackCoreData,
          healthGoals,
          notes,
          isEditMode ? stackId : null
        );

        // Log analytics event based on mode.
        //
        // **Every event this flow emits carries `surface`** (STACK-544). The
        // route label cannot separate an analysis save from an ordinary edit:
        // `/edit-stack/:id` hosts both, and `update_stack` fires from each.
        // `origin` cannot either — it describes the stack, not the surface the
        // member was on.
        const analyticsSurface = reanalyzesOnSave ? ANALYTICS_SURFACE : undefined;
        if (isEditMode) {
          logAnalyticsEvent('update_stack', {
            stack_id: stackId,
            number_of_supplements: rows.length,
            health_goals: healthGoals,
            supplement_ids: rows.map(s => s.key),
            ...(analyticsSurface ? { surface: analyticsSurface } : {})
          });
        } else {
          logAnalyticsEvent('save_stack', {
            origin: ANALYZE_ORIGIN,
            surface: ANALYTICS_SURFACE,
            number_of_supplements: rows.length,
            health_goals: healthGoals,
            supplement_ids: rows.map(s => s.key)
          });
        }

        // Clear relevant localStorage only if creating a new stack (tempId logic)
        if (!isEditMode) {
          const params = new URLSearchParams(window.location.search);
          const tempId = params.get('tempId');
          if (tempId) {
            localStorage.removeItem(`unsavedStack_${tempId}`);
          }
        }

        setErrorMessage('');
        navigate('/dashboard');
        return true;

      } catch (error) {
        console.error("[StackForm persistStack] Error:", error);
        
        let errorMessage = 'Unable to save your stack. Please try again.';
        
        if (error.code === 'permission-denied') {
          errorMessage = 'You don\'t have permission to save this stack. Please sign in again.';
        } else if (error.code === 'unavailable') {
          errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
        } else if (error.message?.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
        
        setErrorMessage(errorMessage);
        return false;
      }
    } else {
      // Navigate to login page if not logged in
      try {
        navigate('/login');
      } catch(navError) {
        console.error("[StackForm persistStack] Error during navigation:", navError);
        setErrorMessage("Failed to navigate. Please try logging in manually.");
      }
      return false;
    }
  };

  /** Start an analysis of what is currently in the form. */
  const runAnalysis = async ({ saveWhenDone }) => {
    if (!healthGoals || !healthGoals.trim()) {
      setErrorMessage(GOALS_REQUIRED);
      return;
    }
    if (selectedSupplements.filter((s) => s.selected !== false).length === 0) {
      setErrorMessage(SUPPLEMENTS_REQUIRED);
      return;
    }

    setErrorMessage('');
    setAnalysisNotice('');
    saveAfterAnalysisRef.current = Boolean(saveWhenDone);
    consumedJobRef.current = null;

    const rowsSent = selectedSupplements;
    const signatureSent = analysisSignature(healthGoals, rowsSent);
    requestedSignatureRef.current = signatureSent;
    const outcome = await analyze({
      healthGoals,
      supplements: rowsSent,
      userProfile,
      startedBy: formKey,
    });

    if (!outcome.started) {
      saveAfterAnalysisRef.current = false;

      // **An edit is saved even when its re-analysis cannot run.** The
      // re-run exists to keep stored results fresh; it must never be the
      // reason a member's own edit is lost. Before this, any refusal — a
      // lapsed member's 403 (the endpoint is members-only), a backend
      // outage, another job in the provider's one slot — left Update showing
      // an error and saving nothing, not even a rename. The stored results
      // are then the last analysis rather than a fresh one.
      if (saveWhenDone) {
        await persistStack();
        return;
      }

      if (outcome.reason !== 'in_flight') {
        setErrorMessage(ANALYSIS_FAILED);
      } else if (outcome.foreignJob) {
        // Blocked by a job this form does not draw progress for — a stack
        // generation, or an analysis another form started — so without a line
        // here the button does nothing and says nothing. An analysis of our
        // own is different: the panel above is already on screen explaining
        // itself, and an error would contradict it.
        setErrorMessage(
          outcome.blockedBy === JOB_KIND ? ANALYSIS_BUSY_ANALYZING : ANALYSIS_BUSY_GENERATING
        );
      }
      return;
    }

    // Completed cache hit: the hook returns the finished body with no job to
    // poll (same shape CreateStack handles on HTTP 200). Merge here so muted
    // notes and dual chips still appear when the request never enters the
    // provider's poll loop.
    if (outcome.syncResult) {
      const merged = mergeAnalysis(rowsSent, outcome.syncResult);
      const citations = outcome.syncResult.citations || null;
      setSelectedSupplements(merged);
      setCitationsData(citations);
      // The rows sent, folded as the result folds them: a supplement entered
      // twice is now one row with the combined amount, and that is what "the
      // same list" means from here. `merged` IS those rows on this path.
      setAnalyzedSignature(analysisSignature(healthGoals, merged));
      setErrorMessage('');
      if (saveAfterAnalysisRef.current) {
        saveAfterAnalysisRef.current = false;
        persistStack(merged, citations);
      }
      return;
    }

    // The rows this analysis is about, kept where a remount can find them.
    // The response identifies supplements by id alone, so merging it into an
    // empty form would produce nothing: a member who navigates away and back
    // has to get their own list returned before the results mean anything.
    startedJobIdRef.current = outcome.jobId;
    rememberPendingRows(outcome.jobId, healthGoals, rowsSent);
  };

  /**
   * The form's primary action.
   *
   * - **Create mode** is the analysis flow: Analyze, then Save once results
   *   are in. A stack cannot be saved from here without an analysis, because
   *   the whole page is the analysis.
   * - **Edit mode on a stack this flow created** re-runs the analysis and
   *   saves when it lands (ruled): the member has changed the list, so the
   *   stored results are about a stack that no longer exists.
   * - **Edit mode on any other stack** saves exactly as it did before.
   */
  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    // Results about a goal or list the member has since changed are not
    // results about this stack, so they cannot be saved as if they were.
    if (!isEditMode && !resultsCurrent) {
      await runAnalysis({ saveWhenDone: false });
      return;
    }

    // Saving needs a name. Checked before a save-time re-analysis starts, so a
    // member is not told about a missing name after waiting for the run.
    if (!requireName()) return;

    // A stack from this flow re-runs its analysis on save — but only when
    // there is still a goal to analyse against. A member who cleared the goal
    // and pressed Update would otherwise be unable to save at all, including a
    // rename, which is a worse outcome than saving without fresh results.
    if (isEditMode && reanalyzesOnSave && healthGoals && healthGoals.trim()) {
      await runAnalysis({ saveWhenDone: true });
      return;
    }

    await persistStack();
  };

  /**
   * The goal field's own submit — its arrow and Enter.
   *
   * **It analyzes; it never saves.** Its accessible name is "Analyze my stack",
   * and it was wired to `handleSubmit`, which once results were in meant
   * *Save*: pressing it saved the stack and left the page, and a member who
   * changed the goal and pressed Enter to re-run saved the old goal's results
   * under the new goal. Saving stays with the footer button, which says so.
   */
  const handleComposerSubmit = () => runAnalysis({ saveWhenDone: false });

  /**
   * Coming back while this form's analysis is still running: put the rows
   * (and goal) it was started with back on screen now, not when it finishes.
   *
   * The remounted form starts empty, and the rows only came back once the
   * result landed — so a member who returned mid-run saw their list gone and
   * reasonably concluded it was lost (Tanu's staging test, case C3). Only an
   * empty form is filled: an edit page loads its own rows, and anything the
   * member has already typed here wins.
   */
  useEffect(() => {
    if (!isAnalyzing || !isInitialLoadComplete || !jobId) return;
    if (startedJobIdRef.current === jobId) return; // this mount started it
    if (selectedSupplements.length > 0) return;
    const pending = recallPendingRows(jobId);
    if (!pending || pending.rows.length === 0) return;
    setSelectedSupplements(pending.rows);
    if (!healthGoals && pending.healthGoals) setHealthGoals(pending.healthGoals);
  // Runs when the job or the load state changes; the row and goal reads are
  // the guard, not triggers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalyzing, isInitialLoadComplete, jobId]);

  /**
   * Consume a finished analysis.
   *
   * Only a job of this flow's kind that **this form** started (`ownsJob`) is
   * read here — the provider holds one job slot for the whole app, shared with
   * stack generation and with every other stack's edit page.
   *
   * **And not before the stack has loaded.** On an edit page the rows arrive
   * from Firestore after mount; consuming first merged the result into an
   * empty list and then the load overwrote it, so a re-run that finished while
   * the member was away was silently discarded. Create mode is "loaded" on its
   * first effect, so this costs it nothing.
   */
  useEffect(() => {
    if (!ownsJob || !isInitialLoadComplete) return;

    if (jobStatus === 'completed' && jobResult) {
      // Render a given job's result once.
      if (consumedJobRef.current === jobId) return;
      consumedJobRef.current = jobId;

      // After a remount the form is empty, so the rows the analysis was about
      // come from where `runAnalysis` left them.
      //
      // **Only after a remount.** "The list is empty" was read as "remounted",
      // so on the mount that started the job, a member who removed every
      // supplement while waiting had them restored when the result landed
      // (PR-Agent, round 7). The mount that started the job always keeps what
      // is on screen, empty or not; the saved rows are for a mount that did
      // not start it and has nothing of its own.
      const pending = recallPendingRows(jobId);
      const startedHere = startedJobIdRef.current === jobId;
      const baseRows = startedHere || selectedSupplements.length > 0
        ? selectedSupplements
        : (pending?.rows || []);
      const merged = mergeAnalysis(baseRows, jobResult);
      const citations = jobResult.citations || null;

      setSelectedSupplements(merged);
      setCitationsData(citations);
      // What these results are about is what was *sent*, not what is on screen
      // now: if the member edited while waiting, the two differ, the results
      // are not current, and the CTA offers Analyze again rather than Save.
      // Sent rows are folded the way the result folds them, so a supplement
      // entered twice — now one row with its combined amount — still counts
      // as the list that was analysed.
      setAnalyzedSignature(
        pending
          ? analysisSignature(pending.healthGoals, mergeAnalysis(pending.rows, jobResult))
          : requestedSignatureRef.current
      );
      // Merging onto the CURRENT rows keeps edits the member made while they
      // waited, but anything they added mid-run carries no results — and an
      // unanalysed row is indistinguishable from one the research had nothing
      // to say about. Say which it is rather than letting a partial analysis
      // read as a complete one.
      const missed = unanalyzedIds(merged, jobResult);
      setErrorMessage('');
      setAnalysisNotice(missed.length > 0 ? ANALYSIS_LIST_CHANGED : '');
      if (!startedHere && !healthGoals && pending?.healthGoals) setHealthGoals(pending.healthGoals);
      forgetPendingRows();
      clearJob();

      if (saveAfterAnalysisRef.current) {
        saveAfterAnalysisRef.current = false;
        // The freshly merged rows are passed explicitly: the setState above
        // has not landed in this tick, so reading state here would save the
        // pre-analysis list.
        persistStack(merged, citations);
      }
      return;
    }

    if (jobStatus === 'failed' || jobStatus === 'expired') {
      const saveAnyway = saveAfterAnalysisRef.current;
      saveAfterAnalysisRef.current = false;
      forgetPendingRows();
      clearJob();
      // Same rule as a refusal in `runAnalysis`: an Update whose re-analysis
      // failed after it started still saves the member's edit.
      if (saveAnyway) {
        persistStack();
        return;
      }
      setErrorMessage(jobError || ANALYSIS_FAILED);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus, jobResult, jobId, ownsJob, isInitialLoadComplete]);

  const handleSupplementChange = (index, field, value) => {
    const newSelectedSupplements = [...selectedSupplements];
    newSelectedSupplements[index] = { ...newSelectedSupplements[index], [field]: value };
    setSelectedSupplements(newSelectedSupplements);
  };

  const addSupplement = (newSupplementData) => {
    setSelectedSupplements([...selectedSupplements, newSupplementData]);
  };

  const removeSupplement = (index) => {
    const newSelectedSupplements = selectedSupplements.filter((_, i) => i !== index);
    setSelectedSupplements(newSelectedSupplements);
  };

  const toggleRowExpansion = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const toggleEditMode = (index) => {
    setEditModeUI(editModeUI === index ? null : index);
    if (editModeUI !== index) {
      setExpandedRow(index);
    }
  };

  const handleSupplementLinkClick = (supplementId, e) => {
    e.preventDefault();
    if (!supplementId) return;
    
    // Navigate to the supplement page
    navigate(`/supplements/${supplementId}`);
    
    // If you need to preserve the stack state when returning:
    // Either use a state management solution or localStorage
    // For example, temporarily store current form state:
    if (isEditMode && stackId) {
      sessionStorage.setItem('returnToEdit', stackId);
      sessionStorage.setItem('formState', JSON.stringify({
        stackName,
        healthGoals,
        notes,
        selectedSupplements
      }));
    }
  };

  const handleAddSupplement = () => {
    // Implement add supplement functionality
  };

  const handleDeleteSupplement = (index) => {
    // Implement delete supplement functionality
  };

  const getStackId = () => stackId;

  /**
   * What the primary button says.
   *
   * Create mode runs Analyze first and only then offers Save — the page *is*
   * the analysis, so there is no path that saves an un-analysed stack from
   * here. The stack name is not required to analyze (there is nothing to name
   * yet), which is why the disabled rule below asks for it only on the save
   * step.
   */
  const primaryAction = isEditMode
    ? 'Update'
    : (resultsCurrent ? 'Save' : SUBMIT_CTA);

  const selectedCount = selectedSupplements.filter((s) => s.selected !== false).length;

  /**
   * What a disabled Analyze is waiting for, or `null`. Only on the Analyze
   * step and never mid-run: while it runs the button already says so, and the
   * Save and Update steps keep the rules they had.
   */
  const analyzeHint = primaryAction === SUBMIT_CTA && !isAnalyzing
    ? analyzeHintText({
      hasGoal: Boolean(healthGoals && healthGoals.trim()),
      hasSupplements: selectedCount > 0,
    })
    : null;

  /**
   * The same hint on the Save and Update steps, for the one thing they wait
   * on that the member can miss: a stack name. Save was disabled with nothing
   * saying why (Tanu, 2026-09-25). The disabled rule itself is unchanged.
   */
  const footerHint = analyzeHint
    || (primaryAction !== SUBMIT_CTA && !isAnalyzing && !stackName ? SAVE_HINT_NEEDS_NAME : null);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container component="main" maxWidth="md" sx={{ px: { xs: '0.5rem', sm: '1rem' } }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          mt: { xs: 2, sm: 4 },
          borderRadius: '1rem',
          background: '#fff',
          boxShadow: '0 0.125rem 0.75rem rgba(0,0,0,0.08)',
        }}
      >
        {/* The analysis page takes the handoff's header (2026-09-24); the edit
            page keeps the one it has always had. */}
        {!isEditMode ? (
          <AnalyzeTitle>{PAGE_TITLE}</AnalyzeTitle>
        ) : (
        <Typography
          component="h1"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: '1.5rem',
            letterSpacing: '0.00625rem',
            background: 'linear-gradient(90deg, #9EB384 15%, #435334 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0.0625rem 0.125rem rgba(0,0,0,0.1)',
            textAlign: 'center',
            mb: 2
          }}
        >
          Edit Stack
        </Typography>
        )}

        {/* Stack Name Section. The analysis surface uses the handoff's labelled
            field; every other stack keeps the caption-and-pencil it had. */}
        {reanalyzesOnSave ? (
          <StackNameField
            value={stackName}
            onChange={handleNameChange}
            missing={nameMissing}
            inputRef={nameInputRef}
          />
        ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap'
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ minWidth: 'fit-content' }}
            >
              Stack Name<RequiredMark />:
            </Typography>
            <TextField
              value={stackName}
              onChange={handleNameChange}
              inputRef={nameInputRef}
              {...nameFieldErrorProps(nameMissing)}
              variant="standard"
              fullWidth
              required
              sx={{
                maxWidth: { xs: '100%', sm: '25rem' },
                '& .MuiInput-root': {
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  '&:before': {
                    borderBottom: '0.125rem solid #E2E8F0'
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: '0.125rem solid #CBD5E0'
                  },
                  '&.Mui-focused:after': {
                    borderBottom: '0.125rem solid #4299E1'
                  }
                }
              }}
              // The visible "Stack Name:" sits beside the field rather than
              // labelling it, so the input had no accessible name at all.
              inputProps={{ 'aria-label': 'Stack name' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      sx={{ 
                        color: '#A0AEC0',
                        padding: '0.25rem',
                        '&:hover': {
                          color: '#4A5568'
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </Box>
        )}

        {/* Health Goals Section.

            **The same component the AI form uses** (ruled: the goals field is
            identical to Create with myStack AI — single goal, free text, same
            validation). It is `StackComposer`, not a copy of it, so the two
            surfaces cannot drift: the chip rail, the rotating examples, the
            Enter-to-submit and the empty-input guard are one implementation.

            `surface="analyze"` is what puts this flow's label on the
            composer's own analytics (`composer_submit`, `input_health_goal`,
            `composer_goal_chip_clicked`). `ownSubmit={false}` (Analyze Stack
            handoff, 2026-09-24) removes the box's own submit: no arrow, Enter
            starts nothing, and a chip fills the goal rather than running — the
            footer's Analyze is the page's one action. */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={reanalyzesOnSave ? SECTION_HEADING_SX : {
            fontSize: '1rem',
            fontWeight: 600,
            color: '#2D3748',
            mb: 2
          }}>
            {GOALS_LABEL}
          </Typography>
          {reanalyzesOnSave ? (
            <StackComposer
              healthGoals={healthGoals}
              setHealthGoals={setHealthGoals}
              onSubmit={handleComposerSubmit}
              loading={isAnalyzing}
              surface={ANALYTICS_SURFACE}
              showHeader={false}
              submitLabel={COMPOSER_SUBMIT_LABEL}
              ownSubmit={false}
            />
          ) : (
            /* Editing a stack this flow did not create. The goals field is
               ruled to change for the *analysis* surface; a stack generated or
               built some other way keeps the field it has always had, because
               nothing about this ticket is about that surface. */
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="What are your health goals? This helps AI provide better recommendations."
              value={healthGoals}
              onChange={(e) => setHealthGoals(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                  backgroundColor: '#F7FAFC',
                  '&:hover': {
                    backgroundColor: '#EDF2F7',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#F7FAFC',
                    boxShadow: '0 0 0 0.125rem rgba(66, 153, 225, 0.2)',
                  }
                }
              }}
            />
          )}
        </Box>

        {/* Live progress — the generator's own panel, reused (ruled). The
            member is waiting on the same kind of work and there is no reason
            for a second vocabulary of waiting. `fallbackLabel` names this
            flow, because the pre-202 window has no server text to show yet and
            the generator's seeded label would say the wrong thing. */}
        {isAnalyzing && (
          <Box sx={{ mt: 2 }} data-testid="analysis-progress">
            <AgentActivityPanel
              progress={liveProgress}
              fallbackLabel={jobStatus === 'starting' ? 'Starting your stack analysis' : ANALYZING}
              resetKey={jobId || 'starting'}
              complete={jobStatus === 'completed'}
            />
          </Box>
        )}

        {/* Supplements Table Section */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={reanalyzesOnSave ? SECTION_HEADING_SX : {
            fontSize: '1rem',
            fontWeight: 600,
            color: '#2D3748',
            mb: 2,
            px: { xs: '0.5rem', sm: '0rem' }
          }}>
            {reanalyzesOnSave ? SUPPLEMENTS_HEADING : 'Your Supplements'}
          </Typography>

          <SupplementsTable
            selectedSupplements={selectedSupplements}
            setSelectedSupplements={setSelectedSupplements}
            handleMainLinkClick={handleSupplementLinkClick}
            citations={citationsData}
            handleDrawerLinkClick={() => {}}
            getStackId={() => stackId || 'manual'}
            stackVersion={stackVersionState}
            showAnalysis={reanalyzesOnSave}
            analyzing={isAnalyzing}
          />
        </Box>

        {/* Notes Section */}
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add notes about your stack (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
                backgroundColor: '#F7FAFC',
                '&:hover': {
                  backgroundColor: '#EDF2F7',
                },
                '&.Mui-focused': {
                  backgroundColor: '#F7FAFC',
                  boxShadow: '0 0 0 0.125rem rgba(66, 153, 225, 0.2)',
                }
              }
            }}
          />

          {/* A note about the analysis: muted, like the notes under the list,
              because nothing failed. */}
          {analysisNotice && (
            <Typography
              data-testid="analysis-notice"
              sx={{ mt: r(8), fontSize: r(11.5), lineHeight: 1.55, color: color.inkMuted }}
            >
              {analysisNotice}
            </Typography>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Typography 
              color="error" 
              variant="body2" 
              sx={{ mt: 1 }}
            >
              {errorMessage}
            </Typography>
          )}
        </Box>

        {/* Footer Buttons. On the Analyze step the button waits for a goal
            and at least one supplement, and a muted hint beside it names what
            is missing (handoff, 2026-09-24). */}
        <Box sx={{
          mt: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'flex-end',
          alignItems: { sm: 'center' },
          gap: 2
        }}>
          {footerHint && <AnalyzeHint>{footerHint}</AnalyzeHint>}
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
            fullWidth={isMobile}
            sx={buttonStyles.secondary}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={(event) => { 
              handleSubmit(event); 
            }}
            disabled={
              isAnalyzing
              || selectedCount === 0
              || Boolean(analyzeHint)
            }
            fullWidth={isMobile}
            sx={{
              // Matches the create-stack Save: one action colour from the
              // design system, with dark ink because white on it fails AA.
              backgroundColor: color.accent,
              color: color.accentInk,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: color.accentHover,
                opacity: 1,
              },
              '&:disabled': {
                backgroundColor: color.line,
                color: color.inkMuted,
              }
            }}
          >
            {isAnalyzing ? ANALYZING : primaryAction}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default StackForm;